import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { Layout } from './components/layout/Layout';
import { DashboardPage } from './app/dashboard/page';
import { WagersPage } from './app/wagers/page';
import { WagerDetailPage } from './app/wagers/[id]/page';
import { CreateWagerPage } from './app/wagers/create/page';
import { ChallengePage } from './app/c/[code]/page';
import { LobbyPage } from './app/lobbies/[game]/page';
import { ProfilePage } from './app/profile/[id]/page';
import { NotificationsPage } from './app/notifications/page';
import { SettingsPage } from './app/settings/page';
import { OnboardingPage } from './app/onboarding/page';
import { LandingPage } from './app/landing/page';
import { AdminPage } from './app/admin/page';
import { FriendsPage } from './app/friends/page';
import { MessagesPage } from './app/messages/page';
import { ChatPage } from './app/messages/[id]/page';
import { Toaster } from './components/ui/toaster';
import { NotificationToastProvider } from './components/NotificationToastProvider';
import { useAuth } from './hooks/useAuth';
import { useAuthStore } from './stores/auth.store';
import { api } from './lib/api';

function AccessGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(() => {
    const stored = sessionStorage.getItem('pullup_invite_code');
    return stored || '';
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Admins and users with access pass through
  if (user?.accessGranted || user?.role === 'admin' || user?.role === 'super_admin') {
    return <>{children}</>;
  }

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || isSubmitting) return;

    setError('');
    setIsSubmitting(true);

    try {
      await api.redeemInviteCode(code.trim());
      sessionStorage.removeItem('pullup_invite_code');
      // Update user in store with access granted
      if (user) {
        setUser({ ...user, accessGranted: true });
      }
      // Check if new user needs onboarding
      try {
        const accounts = await api.getGameAccounts();
        if (accounts.length === 0) {
          navigate('/onboarding', { replace: true });
          return;
        }
      } catch {
        // If check fails, don't block — they'll land on dashboard
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to redeem code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Welcome to PullUp</h1>
          <p className="text-muted-foreground">
            Enter your invite code to get access to the platform.
          </p>
        </div>

        <form onSubmit={handleRedeem} className="space-y-4">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError('');
            }}
            placeholder="Enter invite code"
            maxLength={20}
            className="w-full rounded-lg border border-border bg-card px-4 py-3 text-center text-lg font-mono tracking-widest placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
            autoFocus
          />

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <button
            type="submit"
            disabled={!code.trim() || isSubmitting}
            className="w-full rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Redeeming...' : 'Redeem Code'}
          </button>
        </form>

        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Don't have an invite code? Join the waitlist at{' '}
            <button onClick={logout} className="text-primary hover:underline">
              pullup.gg
            </button>
          </p>
          <button
            onClick={logout}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const { authenticated, ready } = usePrivy();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Store the invite code so AccessGate can pick it up after login
  useEffect(() => {
    if (code) {
      sessionStorage.setItem('pullup_invite_code', code.toUpperCase());
    }
  }, [code]);

  // If already authenticated, go to dashboard (AccessGate will handle the code)
  useEffect(() => {
    if (ready && authenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [ready, authenticated, navigate]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Not authenticated — show sign-in page with the code displayed
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center space-y-6">
        <div className="flex h-12 w-12 mx-auto items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-cyan-500">
          <span className="font-heading text-2xl font-bold text-white">P</span>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">You've been invited</h1>
          <p className="text-muted-foreground text-sm">
            Sign in to redeem your invite code and get access to PullUp.
          </p>
        </div>
        {code && (
          <div className="rounded-lg border border-border bg-card/50 px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">Invite Code</p>
            <p className="text-lg font-mono font-bold tracking-widest text-primary">{code.toUpperCase()}</p>
          </div>
        )}
        <button
          onClick={login}
          className="w-full rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          Sign In & Redeem
        </button>
        <p className="text-xs text-muted-foreground">
          New here? Signing in will create your account automatically.
        </p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { authenticated, ready } = usePrivy();
  const isLoading = useAuthStore((s) => s.isLoading);

  if (!ready || (authenticated && isLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const { authenticated, ready } = usePrivy();

  // Sync Privy auth with backend (exchanges Privy token for app JWT,
  // sets api token, and redirects new users to onboarding)
  useAuth();

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={authenticated ? <Navigate to="/dashboard" /> : <LandingPage />} />
        <Route path="/invite/:code" element={<InvitePage />} />
        <Route path="/c/:code" element={<ChallengePage />} />

        {/* Onboarding (protected but no sidebar layout) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AccessGate>
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/wagers" element={<WagersPage />} />
                    <Route path="/wagers/create" element={<CreateWagerPage />} />
                    <Route path="/wagers/:id" element={<WagerDetailPage />} />
                    <Route path="/lobbies/:game" element={<LobbyPage />} />
                    <Route path="/profile/:id" element={<ProfilePage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/friends" element={<FriendsPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/messages/:id" element={<ChatPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                  </Routes>
                </Layout>
              </AccessGate>
            </ProtectedRoute>
          }
        />
      </Routes>
      <NotificationToastProvider />
      <Toaster />
    </>
  );
}
